import { Mic2, Radio, Heart, Mail, Users, User } from "lucide-react";

// Dynamic glass card style from custom card color
const hexToRgb = (hex) => {
  const h = (hex || "#FFFFFF").replace("#", "");
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
};
const cardStyle = (cc) => {
  const [r,g,b] = hexToRgb(cc);
  return {
    background: `rgba(${r},${g},${b}, 0.06)`,
    border: `1px solid rgba(${r},${g},${b}, 0.13)`,
    borderRadius: "1rem",
  };
};

// Real sky background images by weather condition
const SKY_BG = {
  clear: "https://images.unsplash.com/photo-1638573075661-d6a99a0c8aec?w=600&h=300&fit=crop&crop=top",
  cloudy: "https://images.unsplash.com/photo-1652235888417-f671389f0d72?w=600&h=300&fit=crop&crop=center",
  overcast: "https://images.unsplash.com/photo-1657547481327-afe604a17563?w=600&h=300&fit=crop&crop=center",
  rain: "https://images.unsplash.com/photo-1739242814292-838844f40229?w=600&h=300&fit=crop&crop=center",
  storm: "https://images.unsplash.com/photo-1739242814292-838844f40229?w=600&h=300&fit=crop&crop=top",
  snow: "https://images.unsplash.com/photo-1652235888411-6e3291545470?w=600&h=300&fit=crop&crop=center",
};

// Meteocons CDN (animated SVG icons by basmilius)
const ICON_CDN = "https://cdn.jsdelivr.net/gh/basmilius/weather-icons@2.0.0/production/fill/all";

function wmoToIcon(code) {
  const c = Number(code);
  const map = {
    0: "clear-day", 1: "clear-day", 2: "partly-cloudy-day", 3: "overcast",
    45: "fog", 48: "fog",
    51: "drizzle", 53: "drizzle", 55: "drizzle",
    61: "rain", 63: "rain", 65: "extreme-rain",
    66: "sleet", 67: "sleet",
    71: "snow", 73: "snow", 75: "extreme-snow", 77: "hail",
    80: "partly-cloudy-day-rain", 81: "rain", 82: "extreme-rain",
    85: "partly-cloudy-day-snow", 86: "extreme-snow",
    95: "thunderstorms", 96: "thunderstorms-rain", 99: "thunderstorms-rain",
  };
  return map[c] || "not-available";
}

function descToIcon(desc) {
  const d = (desc || "").toLowerCase();
  if (d.includes("thunder") || d.includes("storm")) return "thunderstorms";
  if (d.includes("heavy rain")) return "extreme-rain";
  if (d.includes("rain") || d.includes("shower")) return "rain";
  if (d.includes("drizzle")) return "drizzle";
  if (d.includes("snow")) return "snow";
  if (d.includes("fog") || d.includes("mist") || d.includes("haz")) return "fog";
  if (d.includes("overcast")) return "overcast";
  if (d.includes("cloud") || d.includes("partly")) return "partly-cloudy-day";
  if (d.includes("clear") || d.includes("sunny")) return "clear-day";
  return "partly-cloudy-day";
}

function getSkyBg(desc) {
  const d = (desc || "").toLowerCase();
  if (d.includes("thunder") || d.includes("storm")) return SKY_BG.storm;
  if (d.includes("rain") || d.includes("drizzle") || d.includes("shower")) return SKY_BG.rain;
  if (d.includes("snow") || d.includes("ice") || d.includes("sleet")) return SKY_BG.snow;
  if (d.includes("overcast") || d.includes("fog")) return SKY_BG.overcast;
  if (d.includes("cloud") || d.includes("partly")) return SKY_BG.cloudy;
  return SKY_BG.clear;
}

const WeatherIcon = ({ code, description, size = 48 }) => {
  const iconName = code !== undefined ? wmoToIcon(code) : descToIcon(description);
  return (
    <img
      src={`${ICON_CDN}/${iconName}.svg`}
      alt={description || "weather"}
      width={size}
      height={size}
      style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.2))" }}
    />
  );
};

export const WeatherWidget = ({ weather, pc, loading, cityName, forecast, showBackground = true, tc, cc }) => {
  const condition = weather?.description || weather?.condition || "";
  const bgUrl = getSkyBg(condition);
  const hours = forecast || [];

  // No-background variant: plain glass card like other widgets
  if (!showBackground) {
    return (
      <div className="p-5 rounded-2xl" style={cardStyle(cc)} data-testid="weather-widget">
        {weather ? (
          <>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold tracking-wide" style={{ color: `${tc || "#FFF"}99` }}>{weather.city || cityName || "Your City"}</p>
                <p className="text-4xl font-light leading-none tracking-tight mt-1" style={{ color: tc || "#FFF" }}>
                  {weather.temp_f || weather.temp || weather.temperature}<span className="text-2xl">°</span>
                </p>
                <p className="text-sm mt-1.5 capitalize" style={{ color: `${tc || "#FFF"}80` }}>{condition}</p>
              </div>
              <WeatherIcon code={weather.weather_code} description={condition} size={64} />
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-white/40">
              <span>Feels {weather.feels_like_f || weather.feels_like || weather.feelsLike}°</span>
              <span>{weather.humidity}%</span>
              <span>{weather.wind_mph || weather.wind_speed || weather.windSpeed} mph</span>
            </div>
            {hours.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-around">
                {hours.slice(0, 5).map((h, i) => (
                  <div key={i} className="flex flex-col items-center gap-1 px-1">
                    <span className="text-white/40 text-[10px] font-semibold">{h.time}</span>
                    <WeatherIcon code={h.iconCode} description={h.condition} size={24} />
                    <span className="text-white/70 text-xs font-bold">{h.temp}°</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center gap-3">
            <WeatherIcon code={undefined} description="" size={28} />
            <p className="text-white/50 text-xs">{loading ? "Loading weather..." : "Weather unavailable"}</p>
          </div>
        )}
      </div>
    );
  }

  // Background variant: sky photo with glass card border
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={cardStyle(cc)}
      data-testid="weather-widget"
    >
      <div className="relative" style={{ minHeight: 140 }}>
        <div className="absolute inset-0">
          <img src={bgUrl} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/50" />
        </div>

        {weather ? (
          <div className="relative z-10 px-5 pt-4 pb-3">
            <p className="text-white/90 text-sm font-semibold tracking-wide drop-shadow">{weather.city || cityName || "Your City"}</p>
            <div className="flex items-center justify-between mt-1">
              <div>
                <p className="text-white text-5xl font-light leading-none tracking-tight drop-shadow-lg">
                  {weather.temp_f || weather.temp || weather.temperature}<span className="text-3xl">°</span>
                </p>
                <p className="text-white/80 text-sm mt-2 capitalize drop-shadow">{condition}</p>
              </div>
              <WeatherIcon code={weather.weather_code} description={condition} size={72} />
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-white/60 drop-shadow">
              <span>Feels {weather.feels_like_f || weather.feels_like || weather.feelsLike}°</span>
              <span>{weather.humidity}%</span>
              <span>{weather.wind_mph || weather.wind_speed || weather.windSpeed} mph</span>
            </div>
          </div>
        ) : (
          <div className="relative z-10 p-5 flex items-center gap-3">
            <WeatherIcon code={undefined} description="" size={28} />
            <p className="text-white/50 text-xs drop-shadow">{loading ? "Loading weather..." : "Weather unavailable"}</p>
          </div>
        )}
      </div>

      {weather && hours.length > 0 && (
        <div className="bg-white/[0.03] backdrop-blur-md px-3 py-3 flex items-center justify-around border-t border-white/[0.08]">
          {hours.slice(0, 5).map((h, i) => (
            <div key={i} className="flex flex-col items-center gap-1 px-1">
              <span className="text-white/60 text-[10px] font-semibold">{h.time}</span>
              <WeatherIcon code={h.iconCode} description={h.condition} size={26} />
              <span className="text-white text-xs font-bold drop-shadow">{h.temp}°</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const OnAirWidget = ({ isLive, djName, np, pc, sc, scheduledShow, tc, cc }) => (
  <div className="p-5 rounded-2xl" style={cardStyle(cc)} data-testid="on-air-widget">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: isLive || scheduledShow ? "#ef4444" : sc }} />
      <h3 className="text-white font-bold text-xs uppercase tracking-widest">On Air Now</h3>
    </div>
    {isLive && djName ? (
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${pc}22` }}>
          <Mic2 className="w-5 h-5" style={{ color: pc }} />
        </div>
        <div>
          <p className="text-white font-semibold">{djName}</p>
          <p className="text-white/35 text-xs">Live DJ</p>
        </div>
      </div>
    ) : scheduledShow ? (
      <div className="flex items-center gap-3">
        {scheduledShow.dj_image ? (
          <img src={scheduledShow.dj_image} alt={scheduledShow.dj_name} className="w-11 h-11 rounded-full object-cover border-2" style={{ borderColor: `${pc}44` }} />
        ) : (
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${pc}22` }}>
            <Mic2 className="w-5 h-5" style={{ color: pc }} />
          </div>
        )}
        <div>
          <p className="text-white font-semibold">{scheduledShow.dj_name}</p>
          <p className="text-white/50 text-xs font-medium" style={{ color: pc }}>{scheduledShow.show_name}</p>
          <p className="text-white/25 text-[10px] mt-0.5">Scheduled Show</p>
        </div>
      </div>
    ) : (
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${sc}22` }}>
          <Radio className="w-5 h-5" style={{ color: sc }} />
        </div>
        <div>
          <p className="text-white font-semibold">AutoDJ</p>
          <p className="text-white/35 text-xs">Playing {np?.playlist || "music"}</p>
        </div>
      </div>
    )}
  </div>
);

export const FollowWidget = ({
  isFollowing, user, followEmail, setFollowEmail,
  followLoading, onFollow, pc, sc, tc, cc
}) => (
  <div className="p-5 rounded-2xl" style={cardStyle(cc)} data-testid="follow-widget">
    <h3 className="font-bold text-xs uppercase tracking-widest mb-3" style={{ color: tc || "#FFF" }}>Follow This Station</h3>
    {isFollowing ? (
      <div className="flex items-center gap-2 text-sm" style={{ color: sc }}>
        <Heart className="w-4 h-4 fill-current" /> Following
      </div>
    ) : user ? (
      <button
        onClick={onFollow}
        disabled={followLoading}
        className="w-full py-2.5 rounded-xl text-white text-sm font-medium transition hover:brightness-110"
        style={{ background: pc }}
        data-testid="station-follow-btn"
      >
        {followLoading ? "..." : "Follow"}
      </button>
    ) : (
      <div className="space-y-2">
        <input
          type="email"
          value={followEmail}
          onChange={e => setFollowEmail(e.target.value)}
          placeholder="Enter your email"
          className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none focus:border-white/25"
          data-testid="station-follow-email"
        />
        <button
          onClick={onFollow}
          disabled={followLoading || !followEmail}
          className="w-full py-2.5 rounded-xl text-white text-sm font-medium transition disabled:opacity-40 hover:brightness-110"
          style={{ background: pc }}
          data-testid="station-follow-submit"
        >
          {followLoading ? "..." : "Follow Station"}
        </button>
      </div>
    )}
  </div>
);

export const SponsorWidget = ({ config, pc, tc, cc }) => (
  <div className="p-5 rounded-2xl text-center" style={cardStyle(cc)} data-testid="sponsor-widget">
    <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: `${tc || "#FFF"}40` }}>Sponsored By</p>
    <p className="font-bold" style={{ color: tc || "#FFF" }}>{config.sponsor_name}</p>
    {config.sponsor_tagline && <p className="text-sm mt-1" style={{ color: `${tc || "#FFF"}66` }}>{config.sponsor_tagline}</p>}
    {config.sponsor_link && (
      <a href={config.sponsor_link} target="_blank" rel="noopener noreferrer"
        className="inline-block mt-3 px-4 py-2 rounded-lg text-white text-xs font-medium transition hover:brightness-110"
        style={{ background: pc }}>
        Learn More
      </a>
    )}
  </div>
);

const SCHED_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmtHr = (h, m) => { const ap = h >= 12 ? "PM" : "AM"; const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h; return `${h12}:${String(m).padStart(2, "0")} ${ap}`; };

export const ScheduleWidget = ({ pc, shows, onAirShowId, onDJClick, tc, cc }) => {
  if (!shows || shows.length === 0) {
    return (
      <div className="p-5 rounded-2xl" style={cardStyle(cc)} id="section-schedule" data-testid="schedule-widget">
        <h3 className="font-bold text-xs uppercase tracking-widest mb-2" style={{ color: tc || "#FFF" }}>Show Schedule</h3>
        <p className="text-sm" style={{ color: `${tc || "#FFF"}40` }}>No shows scheduled yet.</p>
      </div>
    );
  }

  // Group by day, show today first
  const today = new Date().getDay(); // 0=Sun, need to convert: Sun=6, Mon=0..Sat=5
  const todayIdx = today === 0 ? 6 : today - 1;
  const grouped = {};
  shows.forEach(s => {
    if (!grouped[s.day_of_week]) grouped[s.day_of_week] = [];
    grouped[s.day_of_week].push(s);
  });

  // Sort days starting from today
  const sortedDays = [];
  for (let i = 0; i < 7; i++) {
    const d = (todayIdx + i) % 7;
    if (grouped[d]?.length) sortedDays.push(d);
  }

  return (
    <div className="p-5 rounded-2xl" style={cardStyle(cc)} id="section-schedule" data-testid="schedule-widget">
      <h3 className="font-bold text-xs uppercase tracking-widest mb-3" style={{ color: tc || "#FFF" }}>Show Schedule</h3>
      <div className="space-y-3">
        {sortedDays.map(dayIdx => (
          <div key={dayIdx}>
            <p className="text-white/30 text-[10px] uppercase tracking-widest font-bold mb-1.5">
              {dayIdx === todayIdx ? "Today" : SCHED_DAYS[dayIdx]}
            </p>
            {grouped[dayIdx]
              .sort((a, b) => a.start_hour * 60 + a.start_minute - (b.start_hour * 60 + b.start_minute))
              .map((s, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2.5 py-1.5 px-2 rounded-lg mb-1 ${s.show_id === onAirShowId ? "bg-white/[0.08]" : ""}`}
                >
                  {s.show_id === onAirShowId && (
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                  )}
                  {s.dj_image ? (
                    <img
                      src={s.dj_image} alt={s.dj_name}
                      className={`w-7 h-7 rounded-full object-cover flex-shrink-0 border border-white/10 ${s.dj_id && onDJClick ? "cursor-pointer hover:ring-2 hover:ring-purple-500/40" : ""}`}
                      onClick={e => { if (s.dj_id && onDJClick) { e.stopPropagation(); onDJClick(s.dj_id); } }}
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                      <User className="w-3.5 h-3.5 text-white/20" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-medium truncate">{s.show_name}</p>
                    <p className="text-white/30 text-[10px] truncate">{s.dj_name}</p>
                  </div>
                  <span className="text-white/20 text-[10px] flex-shrink-0">
                    {fmtHr(s.start_hour, s.start_minute)}
                  </span>
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const DonationsWidget = ({ pc, tc, cc }) => (
  <div className="p-5 rounded-2xl text-center" style={cardStyle(cc)} data-testid="donations-widget">
    <h3 className="font-bold text-xs uppercase tracking-widest mb-2" style={{ color: tc || "#FFF" }}>Support This Station</h3>
    <p className="text-xs mb-3" style={{ color: `${tc || "#FFF"}4D` }}>Help keep the music playing</p>
    <button className="px-5 py-2 rounded-xl text-white text-sm font-medium transition hover:brightness-110" style={{ background: pc }}>Donate</button>
  </div>
);

export const MerchWidget = ({ pc, tc, cc }) => (
  <div className="p-5 rounded-2xl text-center" style={cardStyle(cc)} data-testid="merch-widget">
    <h3 className="font-bold text-xs uppercase tracking-widest mb-2" style={{ color: tc || "#FFF" }}>Merchandise</h3>
    <p className="text-xs mb-3" style={{ color: `${tc || "#FFF"}4D` }}>Official station gear</p>
    <button className="px-5 py-2 rounded-xl text-white text-sm font-medium transition hover:brightness-110" style={{ background: pc }}>Shop Now</button>
  </div>
);

export const SocialLinksWidget = ({ socials, tc, cc }) => (
  <div className="p-5 rounded-2xl" style={cardStyle(cc)} data-testid="social-links-widget">
    <h3 className="font-bold text-xs uppercase tracking-widest mb-3" style={{ color: tc || "#FFF" }}>Connect With Us</h3>
    <div className="flex flex-wrap gap-2">
      {Object.entries(socials).map(([key, url]) => {
        if (!url) return null;
        return (
          <a key={key} href={url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition text-white/50 hover:text-white text-xs capitalize">
            {key}
          </a>
        );
      })}
    </div>
  </div>
);

export const TopFansWidget = ({ pc, tc, cc }) => (
  <div className="p-5 rounded-2xl" style={cardStyle(cc)} data-testid="top-fans-widget">
    <h3 className="font-bold text-xs uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: tc || "#FFF" }}>
      <Users className="w-4 h-4" style={{ color: pc }} /> Top Listeners
    </h3>
    <p className="text-sm" style={{ color: `${tc || "#FFF"}40` }}>Listen more to climb the leaderboard!</p>
  </div>
);

export const ContactWidget = ({ pc, tc, cc }) => (
  <div className="p-5 rounded-2xl" style={cardStyle(cc)} id="section-contact" data-testid="contact-widget">
    <h3 className="font-bold text-xs uppercase tracking-widest mb-3 flex items-center gap-2" style={{ color: tc || "#FFF" }}>
      <Mail className="w-4 h-4" style={{ color: pc }} /> Contact Us
    </h3>
    <div className="space-y-2">
      <input type="text" placeholder="Your name"
        className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none" />
      <input type="email" placeholder="Your email"
        className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none" />
      <textarea placeholder="Your message..." rows={3}
        className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none resize-none" />
      <button className="w-full py-2 rounded-xl text-white text-sm font-medium transition hover:brightness-110" style={{ background: pc }}>
        Send Message
      </button>
    </div>
  </div>
);

export const LaunchCtaWidget = ({ pc, sc, tc, cc }) => (
  <div className="rounded-2xl p-5 text-center" style={{ background: `linear-gradient(135deg, ${pc}18, ${sc}18)`, border: `1px solid ${pc}22` }} data-testid="launch-cta-widget">
    <Radio className="w-7 h-7 mx-auto mb-2" style={{ color: pc }} />
    <p className="text-white font-bold text-sm">Start Your Own Station</p>
    <p className="text-white/30 text-xs mt-1 mb-3">Get your own branded radio page like this one</p>
    <a href="/#pricing" className="inline-block px-5 py-2 rounded-full text-white text-xs font-bold transition hover:brightness-110" style={{ background: pc }}>
      Get Started
    </a>
  </div>
);
